import * as React from 'react';
import { useQueryInfo } from 'client/shared/containers/query';
import * as QueryInfos from 'client/shared/graphql-queries/query-infos';
import {
  useRedirect,
  useBrowser,
  useLanguageByQueryParam,
} from 'client/shared/hooks';
import { useMutationInfo } from 'client/shared/containers/mutation';
import * as MutationInfos from 'client/shared/graphql-mutations/mutation-infos';
import * as RespondentContext from 'client/respondent/core/reducers/context';
import { convertToQuestionVotingProps } from 'client/respondent/voting/shared/functions';
import { RouteComponentProps, Redirect } from 'react-router';
import {
  useCurrentRespondent,
  CurrentRespondent,
  CurrentUser,
} from 'client/respondent/hooks';
import { ClientQuestionId, QuestionType } from 'client/shared/core/question';
import {
  EmbedQuestion,
  EmbedQuestionProps,
} from 'client/embed/components/embed-question';
import { VotingProps } from 'client/respondent/core/types';
import { EmbedHeader } from 'client/embed/components/embed-header';
import { EmbedPublisher } from 'client/embed/components/embed-publisher';
import { ClientUrlUtils } from 'client/shared/core/helpers';
import { LoadedEvents, VotingInteraction } from 'client/shared/core/types';
import { publishingEntityPrimaryLogoUrl } from 'client/shared/core/publishing-entity';
import { QuestionSetType } from 'core';
import { useLocalStorage } from 'client/shared/helpers/hooks';
import {
  questionSetSlugFromParamsProps,
  pubSlugFromParamsProps,
  questionIdFromParamsProps,
} from 'client/embed/shared/functions';

const UrlUtils = ClientUrlUtils.embed;

export type Props = RouteComponentProps<{
  readonly questionId: string;
  readonly setSlug: string;
  readonly pubSlug: string;
}>;

export const QuestionEmbedPage: React.FC<Props> = (p: Props) => {
  const questionId = questionIdFromParamsProps(p);
  const setSlug = questionSetSlugFromParamsProps(p);
  const pubSlug = pubSlugFromParamsProps(p);
  const currentUser = useCurrentRespondent();
  if (
    CurrentRespondent.ERROR.isA(currentUser) ||
    CurrentRespondent.LOADING.isA(currentUser)
  ) {
    return null;
  }

  const respondentUser =
    CurrentRespondent.RESPONDENT.isA(currentUser) ||
    CurrentRespondent.GUEST_RESPONDENT.isA(currentUser)
      ? currentUser
      : null;

  return (
    <QuestionEmbedInnerPage
      pubSlug={pubSlug}
      questionId={questionId}
      respondentUser={respondentUser}
      setSlug={setSlug}
    />
  );
};
QuestionEmbedPage.displayName = 'QuestionEmbedPage';

interface InnerProps {
  readonly questionId: ClientQuestionId;
  readonly setSlug: string;
  readonly pubSlug: string;
  readonly respondentUser: CurrentUser | null;
}

const QuestionEmbedInnerPage: React.FC<InnerProps> = (p) => {
  const respondentId = p.respondentUser?.user?.respondent?.id;

  const localStorage = useLocalStorage();
  const state = RespondentContext.useRespondentState();
  const dispatch = RespondentContext.useRespondentDispatch();
  const redirect = useRedirect();
  const questionSetQuery = useQueryInfo(QueryInfos.respondentVotingPage, {
    variables: {
      questionSetSlug: p.setSlug,
      publishingEntitySlug: p.pubSlug,
      respondentId: respondentId,
    },
  });
  const qResult = useQueryInfo(QueryInfos.votingPage, {
    variables: {
      respondentId: respondentId,
      questionId: p.questionId,
      pagination: {
        page: 1,
        perPage: 10000,
      },
    },
    notifyOnNetworkStatusChange: true,
  });
  const browser = useBrowser();
  const { selectLanguageText } = useLanguageByQueryParam();

  const refetchUser = respondentId
    ? []
    : [QueryInfos.currentRespondent.refetchInfo({})];
  const refetchQueries = [
    QueryInfos.votingPage.refetchInfo({
      questionId: p.questionId,
      respondentId: respondentId,
      pagination: {
        page: 1,
        perPage: 1000,
      },
    }),
    ...refetchUser,
  ];

  // mutations
  // question
  const questionMuts: MutationInfos.QuestionVotingMutationReturns = {
    voteForQuestion: useMutationInfo(MutationInfos.voteForQuestion, {
      refetchQueries,
    }),
  };

  const props = convertToQuestionVotingProps(
    state,
    dispatch,
    qResult,
    questionMuts,
    redirect,
    questionSetQuery,
    p.respondentUser,
    p.questionId,
    () => {},
    selectLanguageText,
    localStorage
  );

  if (props.type === VotingProps.VotingPropsType.LOADED) {
    const events = !browser.thirdPartyCookiesEnabled()
      ? updateEventsForEmbed(
          p.pubSlug,
          p.setSlug,
          p.questionId,
          props.typedData.questionType === QuestionType.MULTIPLE_CHOICE,
          browser.openNewWindow,
          props.events
        )
      : { ...props.events, onWholeQuestionClick: undefined };
    return (
      <div>
        <EmbedHeader />
        <EmbedPublisher
          pubLogo={publishingEntityPrimaryLogoUrl(props.publishingEntity.assets)}
          pubName={props.publishingEntity.name}
          pubSlug={props.publishingEntity.slug}
        />
        <EmbedQuestion {...props} events={events} />
        <VotingModal {...props} events={events} />
      </div>
    );
  } else {
    return null;
  }
};
QuestionEmbedInnerPage.displayName = 'QuestionEmbedInnerPage';

export const VotingModal: React.FC<VotingProps.QuestionSetLoaded> = (props) => {
  switch (props.votingInteraction) {
    case VotingInteraction.PROMPT_TO_REGISTER:
      return <Redirect to={UrlUtils.signup.path()} />;
    case VotingInteraction.COMMENTING:
    case VotingInteraction.REGISTRATION_SUCCESSFUL:
    case VotingInteraction.VOTE_SUCCESSFUL:
    case VotingInteraction.VIEWING_COMMENTS:
    case VotingInteraction.SELECTING_CHOICE:
    case null:
      // Intentionally not putting a default here, as I think it is good to force us to consider how new functionality should behave
      // in the case of embed. Using default would allow us to ignore that situation.
      return null;
  }
};
VotingModal.displayName = 'VotingModal';

function updateEventsForEmbed(
  pubSlug: string,
  setSlug: string,
  questionId: string,
  isMC: boolean,
  open: (url: string) => void,
  events: LoadedEvents
): EmbedQuestionProps['events'] {
  const url = ClientUrlUtils.common.addQueryParams(
    ClientUrlUtils.respondent.question.url({
      pubSlug,
      setIdOrSlug: setSlug,
      questionId,
      setType: QuestionSetType.SET,
    }),
    {
      utm_medium: 'embed',
      utm_source: document.referrer,
    }
  );
  return {
    ...events,
    selectMultipleChoice(choices) {
      const useUrl =
        choices.length > 0
          ? ClientUrlUtils.common.addQueryParams(url, {
              selection: choices[0].id,
            })
          : url;
      open(useUrl);
    },
    selectPointAllocation() {},
    updateCommentText() {},
    onWholeQuestionClick: isMC
      ? undefined
      : () => {
          open(url);
        },
  };
}
